import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useParams, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X, ArrowUpRight, Phone, ChevronUp, BookOpen } from "lucide-react";
import { fetchCategories, fetchProducts } from "../lib/api";
import { useDocumentMeta } from "../hooks/use-document-meta";
import { useJsonLd } from "../hooks/use-json-ld";
import { ProductCard } from "../components/ProductCard";
import CATEGORY_MANIFESTOS from "../content/manifestos/categories.json";
import { categoryPath, categoryFromUrlSlug } from "../lib/category-slugs";

// Ordre des gammes = ordre du catalogue imprimé (sommaire).
const ORDRE_CATALOGUE = [
  "frites", "specialites", "appetizers", "viandes", "volailles", "sauce",
  "huile", "tortillas-et-pains", "dessert", "produits-laitiers", "conserve", "emballages",
];
const rang = (slug) => {
  const i = ORDRE_CATALOGUE.indexOf(slug);
  return i === -1 ? 99 : i;
};
// Nombre de produits montrés par gamme sur la vue d'ensemble /catalogue.
const APERCU = 10;

export default function Catalog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { categorySlug: urlCategorySlug } = useParams(); // /catalogue/:categorySlug
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [toutesMarques, setToutesMarques] = useState(false);

  // Active category resolution — two URL shapes are accepted:
  //   - /catalogue/:urlSlug   (new, canonical, e.g. /catalogue/sauces)
  //   - /catalogue?categorie=  (legacy, kept alive for back-compat with
  //                            shared/bookmarked links, and the BrandCarousel
  //                            still uses setSearchParams for ?marque=)
  // URL slug wins when both are present.
  const activeCategory = urlCategorySlug
    ? (categoryFromUrlSlug(urlCategorySlug) || urlCategorySlug)
    : (searchParams.get("categorie") || "all");

  const activeBrand = searchParams.get("marque") || "all";
  const hasFilters = activeBrand !== "all" || Boolean(search);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    // Every navigation to a category page lands at the top — no more "scroll
    // to grid" hack. The dedicated /catalogue/<slug> URL is the dedicated
    // experience; the user already chose the category by clicking the link.
    window.scrollTo(0, 0);
    fetchCategories()
      .then((cats) => setCategories(Array.isArray(cats) ? cats : []))
      .catch(() => setCategories([]));
  }, [urlCategorySlug]);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (activeCategory !== "all") params.category = activeCategory;
    if (activeBrand !== "all") params.brand = activeBrand;
    if (search) params.search = search;
    fetchProducts(params)
      .then((p) => setProducts(Array.isArray(p) ? p : []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [activeCategory, activeBrand, search]);

  // Tous les produits actifs : sert aux compteurs réels par gamme et aux
  // marques disponibles dans la gamme ouverte.
  useEffect(() => {
    fetchProducts({}).then((p) => setAllProducts(Array.isArray(p) ? p : [])).catch(() => setAllProducts([]));
  }, []);

  const setBrand = (b) => {
    const p = new URLSearchParams(searchParams);
    if (b === "all") p.delete("marque"); else p.set("marque", b);
    setSearchParams(p);
  };
  const resetAllFilters = () => {
    setSearch("");
    // Brand-only reset stays on the current category page.
    setSearchParams(new URLSearchParams());
  };

  const activeCat = useMemo(
    () => categories.find((c) => c.slug === activeCategory),
    [activeCategory, categories]
  );

  // Canonical now points to the new /catalogue/<url-slug> path. Legacy
  // ?categorie= URLs still resolve (so old links don't 404) but they
  // canonicalize to the clean dedicated page, consolidating SEO signals.
  //
  // Title/description: prefer the manually-written seoTitle/seoDescription
  // on the category JSON (categories.json), fall back to the generic template
  // when those fields are absent. The fallback keeps backwards-compat with any
  // category added in the CMS without explicit SEO copy. Note: the trailing
  // " — Frites Dorées" suffix is appended downstream by useDocumentMeta, so
  // the seoTitle stored in JSON doesn't need to include the brand again.
  const SUFFIX_RE = /\s—\s+Frites Dorées\.?$/;
  const stripSuffix = (s) => (typeof s === "string" ? s.replace(SUFFIX_RE, "").trim() : s);
  const catTitle = activeCat
    ? stripSuffix(activeCat.seoTitle) ||
      `${activeCat.label} grossiste alimentaire B2B restauration rapide`
    : "Catalogue grossiste alimentaire B2B restauration rapide — Frites surgelées, sauces, emballages pros";
  const catDescription = activeCat
    ? activeCat.seoDescription ||
      `${activeCat.label} : grossiste alimentaire B2B et fournisseur restauration rapide. Sélection professionnelle, livraison pros Île-de-France et France selon zone. Devis B2B 24h.`
    : "Grossiste alimentaire B2B et fournisseur restauration rapide : frites surgelées, viandes, volailles, sauces, emballages alimentaires professionnels. 23 marques, près de 300 références. Livraison pros Île-de-France et France selon zone.";
  useDocumentMeta({
    title: catTitle,
    description: catDescription,
    canonicalPath: activeCat ? categoryPath(activeCat.slug) : "/catalogue",
  });

  // ────────────────── Invisible structured data ──────────────────
  // BreadcrumbList: Accueil → Catalogue → <label?>. Helps Google render the
  // breadcrumb in SERP and consolidates the URL hierarchy signal.
  const SITE = "https://www.fritesdorees.fr";
  const breadcrumbLd = useMemo(() => {
    const items = [
      { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Catalogue", item: `${SITE}/catalogue` },
    ];
    if (activeCat) {
      items.push({
        "@type": "ListItem",
        position: 3,
        name: activeCat.label,
        item: `${SITE}${categoryPath(activeCat.slug)}`,
      });
    }
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items,
    };
  }, [activeCat]);
  useJsonLd(`breadcrumb:catalog:${activeCategory || "all"}`, breadcrumbLd);

  // ItemList: on /catalogue lists the 12 categories (CollectionPage flavor);
  // on /catalogue/<slug> lists the filtered products. We cap at 30 items to
  // keep the LD block small. URL strings reuse our slug map so they always
  // match canonical paths and never desync with the sitemap.
  const itemListLd = useMemo(() => {
    if (activeCat) {
      if (!Array.isArray(products) || products.length === 0) return null;
      return {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: `${activeCat.label} — Catalogue grossiste Frites Dorées`,
        itemListOrder: "https://schema.org/ItemListOrderDescending",
        numberOfItems: products.length,
        itemListElement: products.slice(0, 30).map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `${SITE}/produit/${p.slug}`,
          name: p.name,
        })),
      };
    }
    if (!Array.isArray(categories) || categories.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Catalogue grossiste B2B Frites Dorées — 12 gammes",
      numberOfItems: categories.length,
      itemListElement: categories.slice(0, 30).map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE}${categoryPath(c.slug)}`,
        name: c.label,
      })),
    };
  }, [activeCat, products, categories]);
  useJsonLd(`itemlist:catalog:${activeCategory || "all"}`, itemListLd);

  // Gammes dans l'ordre du catalogue, avec le vrai nombre de produits actifs.
  const gammes = useMemo(() => {
    const n = {};
    allProducts.forEach((p) => { n[p.category] = (n[p.category] || 0) + 1; });
    return categories
      .map((c) => ({ ...c, n: n[c.slug] || 0 }))
      .filter((c) => c.n > 0)
      .sort((a, b) => rang(a.slug) - rang(b.slug));
  }, [categories, allProducts]);

  // Marques présentes dans la gamme ouverte (filtre simple, sous les gammes).
  const marques = useMemo(() => {
    const n = {};
    allProducts
      .filter((p) => p.brand && (!activeCat || p.category === activeCategory))
      .forEach((p) => { n[p.brand] = (n[p.brand] || 0) + 1; });
    return Object.entries(n).sort((a, b) => b[1] - a[1]);
  }, [activeCat, activeCategory, allProducts]);

  const manifeste = activeCat ? CATEGORY_MANIFESTOS[activeCategory] : null;
  const vueEnsemble = !activeCat && !search && activeBrand === "all";
  const AUTRES = "Autres produits";
  // Regroupe une liste de produits par marque (vue gamme) ou par gamme
  // (vue marque), dans un ordre stable et lisible.
  const groupes = useMemo(() => {
    if (search || vueEnsemble) return null;
    const g = new Map();
    if (activeCat) {
      products.forEach((p) => {
        const k = p.brand || AUTRES;
        if (!g.has(k)) g.set(k, []);
        g.get(k).push(p);
      });
      return [...g.entries()]
        .sort((a, b) => (a[0] === AUTRES) - (b[0] === AUTRES) || b[1].length - a[1].length)
        .map(([titre, items]) => ({ titre, items }));
    }
    products.forEach((p) => {
      if (!g.has(p.category)) g.set(p.category, []);
      g.get(p.category).push(p);
    });
    return [...g.entries()]
      .sort((a, b) => rang(a[0]) - rang(b[0]))
      .map(([slug, items]) => ({ titre: items[0]?.category_label || slug, items, slug }));
  }, [search, vueEnsemble, activeCat, products]);
  const parGamme = useMemo(() => {
    const g = {};
    allProducts.forEach((p) => { (g[p.category] = g[p.category] || []).push(p); });
    return g;
  }, [allProducts]);

  const chip = (actif) =>
    `shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full border font-mono text-[11px] uppercase tracking-[0.12em] whitespace-nowrap transition-colors ${
      actif
        ? "bg-[#FFC300] border-[#FFC300] text-black"
        : "border-white/15 text-zinc-300 hover:border-[#FFC300] hover:text-[#FFC300]"
    }`;

  return (
    <main data-testid="catalog-page" className="bg-[#050505] min-h-screen">
      {/* 1. En-tête simple : où je suis, combien de produits, recherche. */}
      <header className="pt-24 lg:pt-32 pb-6 lg:pb-8 px-5 md:px-10 lg:px-14">
        <div className="max-w-[1400px] mx-auto">
          <nav className="tag-label mb-4 flex items-center gap-2 text-zinc-500" aria-label="Fil d'Ariane">
            <Link to="/catalogue" className="hover:text-[#FFC300]">Catalogue</Link>
            {activeCat && (
              <>
                <span>/</span>
                <span className="text-zinc-300">{activeCat.label}</span>
              </>
            )}
          </nav>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-3xl">
              <h1
                className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.92]"
                data-testid="catalog-title"
              >
                {activeCat ? activeCat.label : (<>Nos <span className="text-[#FFC300]">produits</span></>)}
              </h1>
              <p className="mt-3 text-sm md:text-base text-zinc-400 leading-relaxed">
                {activeCat
                  ? (manifeste?.manifesto || activeCat.description)
                  : "Grossiste frites surgelées, viandes, volailles, sauces restauration rapide et emballages alimentaires professionnels. Livraison professionnels Île-de-France."}
              </p>
              <p className="mt-3 font-mono text-xs uppercase tracking-[0.14em] text-[#FFC300]">
                {activeCat
                  ? `${gammes.find((g) => g.slug === activeCategory)?.n ?? products.length} produits`
                  : `${allProducts.length} produits · ${gammes.length} gammes`}
              </p>
            </div>
            <div className="flex flex-col gap-3 w-full lg:w-[380px] shrink-0">
              <div className="relative">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none z-10" />
                <input
                  data-testid="catalog-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={activeCat ? `Rechercher dans ${activeCat.label.toLowerCase()}…` : "Rechercher un produit, une marque…"}
                  className="!pl-11 !py-3.5 w-full"
                />
                {search && (
                  <button onClick={() => setSearch("")} aria-label="Effacer" className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                    <X size={16} />
                  </button>
                )}
              </div>
              <Link
                to="/catalogue-feuilletable"
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-400 hover:text-[#FFC300] transition-colors"
              >
                <BookOpen size={14} /> Feuilleter le catalogue 2026 <ArrowUpRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* 2. Boutons : toutes les catégories et toutes les marques, visibles d'un coup. */}
      <section className="border-y border-white/10 bg-[#080808]" data-testid="catalog-filtres">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 py-5 lg:py-6 space-y-5">
          <div>
            <p className="tag-label text-zinc-500 mb-3">Catégories</p>
            <nav className="flex flex-wrap gap-2" aria-label="Catégories" data-testid="catalog-gammes">
              <Link to="/catalogue" className={chip(!activeCat)}>
                Tout <span className="opacity-60">{allProducts.length}</span>
              </Link>
              {gammes.map((c) => (
                <Link key={c.slug} to={categoryPath(c.slug)} className={chip(c.slug === activeCategory)}>
                  {c.label} <span className="opacity-60">{c.n}</span>
                </Link>
              ))}
            </nav>
          </div>
          {marques.length > 1 && (
            <div>
              <p className="tag-label text-zinc-500 mb-3">
                Marques{activeCat ? ` · ${activeCat.label}` : ""}
              </p>
              <div className="flex flex-wrap gap-2" data-testid="catalog-marques">
                <button type="button" onClick={() => setBrand("all")} className={chip(activeBrand === "all")}>
                  Toutes
                </button>
                {marques.map(([m, n], i) => (
                  <button
                    type="button"
                    key={m}
                    onClick={() => setBrand(m)}
                    className={`${chip(activeBrand === m)} ${i >= 6 && !toutesMarques && activeBrand !== m ? "hidden md:inline-flex" : ""}`}
                  >
                    {m} <span className="opacity-60">{n}</span>
                  </button>
                ))}
                {marques.length > 6 && !toutesMarques && (
                  <button type="button" onClick={() => setToutesMarques(true)} className={`${chip(false)} md:hidden !border-dashed`}>
                    + {marques.length - 6} marques
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* 3. Contenu */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 pb-20">
        {vueEnsemble ? (
          // Vue d'ensemble : une bande par gamme, qu'on fait glisser de côté.
          <div className="divide-y divide-white/10">
            {gammes.map((c) => (
              <section key={c.slug} className="py-6 lg:py-8" data-testid={`catalog-gamme-${c.slug}`}>
                <div className="flex items-baseline justify-between gap-4 mb-4">
                  <h2 className="font-display text-xl md:text-3xl uppercase tracking-tighter leading-none">
                    <Link to={categoryPath(c.slug)} className="hover:text-[#FFC300] transition-colors">{c.label}</Link>
                    <span className="ml-2 font-mono text-xs tracking-[0.14em] text-zinc-500 align-middle">{c.n}</span>
                  </h2>
                  <Link
                    to={categoryPath(c.slug)}
                    className="shrink-0 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-[#FFC300] hover:text-white transition-colors"
                  >
                    Voir tout <ArrowUpRight size={14} />
                  </Link>
                </div>
                <div className="-mx-5 md:mx-0 px-5 md:px-0 flex gap-3 md:gap-5 overflow-x-auto snap-x snap-mandatory pb-2 [scrollbar-width:thin]">
                  {(parGamme[c.slug] || []).slice(0, APERCU).map((p, i) => (
                    <div key={p.id} className="snap-start shrink-0 w-[42%] sm:w-[30%] md:w-[22%] lg:w-[17%] xl:w-[15%]">
                      <ProductCard product={p} index={i} size="compact" />
                    </div>
                  ))}
                  {c.n > APERCU && (
                    <Link
                      to={categoryPath(c.slug)}
                      className="snap-start shrink-0 w-[42%] sm:w-[30%] md:w-[22%] lg:w-[17%] xl:w-[15%] aspect-square border border-white/10 flex flex-col items-center justify-center gap-2 text-center text-[#FFC300] hover:bg-[#FFC300] hover:text-black transition-colors"
                    >
                      <ArrowUpRight size={22} />
                      <span className="font-mono text-[11px] uppercase tracking-[0.14em]">Voir les {c.n}</span>
                    </Link>
                  )}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <section className="pt-6 lg:pt-8" data-testid="catalog-grid-section">
            <div className="flex items-center justify-between gap-4 mb-6">
              <p className="tag-label text-zinc-500" data-testid="products-count">
                {loading ? "Chargement…" : `${products.length} produit${products.length > 1 ? "s" : ""}`}
                {activeBrand !== "all" && ` · ${activeBrand}`}
                {search && ` pour « ${search} »`}
              </p>
              {hasFilters && (
                <button
                  onClick={resetAllFilters}
                  className="tag-label inline-flex items-center gap-1.5 text-white/60 hover:text-[#FFC300] transition-colors"
                  data-testid="catalog-reset-all"
                >
                  Effacer les filtres <X size={12} />
                </button>
              )}
            </div>
            {!loading && products.length === 0 ? (
              <div className="py-16 text-center max-w-xl mx-auto" data-testid="catalog-empty">
                <p className="font-display text-2xl md:text-3xl uppercase tracking-tight mb-3">Aucun produit ne correspond.</p>
                <p className="font-body text-zinc-400 mb-6">Vous cherchez une référence précise ? Un commercial vous oriente.</p>
                <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="catalog-empty-cta">
                  Demander un conseil <ArrowUpRight size={14} />
                </Link>
              </div>
            ) : groupes ? (
              <div className="divide-y divide-white/10">
                {groupes.map((g) => (
                  <section key={g.titre} className="py-6 lg:py-8 first:pt-2" data-testid="catalog-groupe">
                    <div className="flex items-end justify-between gap-4 mb-4">
                      <h2 className="font-display text-xl md:text-3xl uppercase tracking-tighter leading-none">
                        {g.titre} <span className="ml-2 font-mono text-xs tracking-[0.14em] text-zinc-500 align-middle">{g.items.length}</span>
                      </h2>
                      {g.slug && (
                        <Link to={categoryPath(g.slug)} className="shrink-0 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-[#FFC300] hover:text-white transition-colors">
                          Voir la catégorie <ArrowUpRight size={14} />
                        </Link>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-5">
                      {g.items.map((p, idx) => (
                        <ProductCard key={p.id} product={p} index={idx} size="compact" />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-5">
                {products.map((p, idx) => (
                  <ProductCard key={p.id} product={p} index={idx} size="compact" />
                ))}
              </div>
            )}
          </section>
        )}
      </div>

      {/* 4. Un seul bloc de contact en fin de page. */}
      <section className="bg-[#FFC300] text-black py-14 lg:py-20 px-5 md:px-10 lg:px-14">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tighter leading-[0.95] max-w-2xl">
            Un besoin précis ? <br /> On chiffre sous 24h.
          </h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/contact"
              className="btn-secondary !border-black !text-black hover:!bg-black hover:!text-[#FFC300]"
              data-testid="catalog-contact-cta"
            >
              Demander un devis <ArrowUpRight size={18} />
            </Link>
            <a href="tel:+33148680303" className="btn-secondary !border-black !text-black hover:!bg-black hover:!text-[#FFC300] inline-flex items-center gap-2">
              <Phone size={16} /> 01 48 68 03 03
            </a>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            key="scroll-top"
            initial={{ opacity: 0, y: 16, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.9 }}
            transition={{ duration: 0.25 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            aria-label="Remonter en haut"
            data-testid="catalog-scroll-top"
            className="fixed bottom-24 right-5 z-40 w-11 h-11 md:w-12 md:h-12 flex items-center justify-center bg-[#FFC300] text-black shadow-[0_12px_32px_-8px_rgba(255,195,0,0.55)] hover:scale-105 active:scale-95 transition-transform"
          >
            <ChevronUp size={20} strokeWidth={2.5} />
          </motion.button>
        )}
      </AnimatePresence>
    </main>
  );
}
